import React, { ReactElement, useEffect, useState } from 'react';
import { staticSidePanelSignal } from '../layout/Layout';
import FormAddressSelector from '../entityComponents/Form/components/FormAddressSelector';
import { httpGet, httpPost } from '../../../modules/Backend';
import HttpStatusCodes from '../../../modules/HttpStatusCodes';
import { HttpStatusCode } from 'axios';
import { IAddress } from '../entityComponents/Form/components/FormAddress';
import AdobeComboBox, { IOption } from '../../AdobeAriaLib/AdobeComboBox';
import AdobeTextField from '../../AdobeAriaLib/AdobeTextField';
import AdobeValueToggle from '../../AdobeAriaLib/AdobeValueToggle';
import { Button } from 'react-aria-components';
import { ValidateEmail } from '../../../Validation/Validation';
import { modalSignal } from "../../../components/app/Modal/ModalContainer";
import ConfirmModal from '../Modal/ConfirmModal';
import { Change } from '../../lib/wizard/Wizard';
import { OrderNumberTypeSmartEnum } from '../../../enums/Domain/OrderNumberTypeSmartEnum';

interface Step3Props {
  displayName: string
  prevStep?: () => void;
  values?: Record<string, any>;
  errors?: Record<string, string>;
  handleChange?: (input: string) => (value: any) => void;
  handleChangeBatched?: (changes: Change[]) => void;
}

const RecallStep3: React.FC<Step3Props> & { validate?: (values: Record<string, any>) => Record<string, string> } = ({
  prevStep,
  values,
  errors,
  displayName,
  handleChange,
  handleChangeBatched
}) => {

  const recallId = values!.recallId;
  const accountId = values!.accountId;
  const [allDeliveryOptions, setAllDeliveryOptions] = useState<any>();
  const [deliveryAddressOptions, setDeliveryOptions] = useState<IOption[]>([]);
  const [deliveryAddresses, setDeliveryAddresses] = useState<Map<string, IAddress>>(new Map());
  const [invoiceAddressOptions, setInvoiceAddressOptions] = useState<IOption[]>([]);
  const [invoiceAddresses, setInvoiceAddresses] = useState<Map<string, IAddress>>(new Map());
  const [activeOrderNumbers, setActiveOrderNumbers] = useState<IOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    staticSidePanelSignal.value = undefined
    checkCanEditAndLoad();
  }, [])

  async function checkCanEditAndLoad() {
    if (recallId) {
      const canEditResult = await httpGet(`api/recalls/canEdit?id=${recallId}`)
      if(canEditResult.status !== HttpStatusCodes.Ok || !canEditResult.data?.canEdit){
        window.history.back()
        return
      }
    }
    getDeliveryOptions();
    getActiveOrderNumbers();
  }

  async function getDeliveryOptions() {
    if (recallId) {
      const timeout = setTimeout(() => setLoading(true), 200);
      const params = new URLSearchParams();
      params.append('id', recallId);
      const result = await httpGet(`api/recalls/getDeliveryOptions?${params.toString()}`);
      if (result.status === HttpStatusCodes.Ok) {

        const initialStateChanges: Change[] = [];

        setAllDeliveryOptions(result.data);
        const deliveryAddressMap = new Map<string, IAddress>();
        const deliveryOptions = result.data.deliveryAddresses.map((x: any) => {
          const data = JSON.parse(x.value);
          deliveryAddressMap.set(data.id, data.address);
          return {
            value: data.id,
            text: x.text
          }
        });
        setDeliveryAddresses(deliveryAddressMap);
        setDeliveryOptions(deliveryOptions);
        if (!values?.deliveryAddress && !values?.isDigital && deliveryOptions.length > 0 && deliveryOptions[0]?.value) {
          initialStateChanges.push({
            elementName: "deliveryAddress",
            value: deliveryOptions[0].value
          });
        }

        const invoiceAddressMap = new Map<string, IAddress>();
        const invoiceOptions = result.data.invoiceAddresses.map((x: any) => {
          const data = JSON.parse(x.value);
          invoiceAddressMap.set(data.id, data.address);
          return {
            value: data.id,
            text: x.text
          }
        });
        setInvoiceAddresses(invoiceAddressMap);
        setInvoiceAddressOptions(invoiceOptions);

        if (!values?.onBehalfOfUser && result.data.defaultOnBehalfOf) {
          initialStateChanges.push({
            elementName: "onBehalfOfUser",
            value: result.data.defaultOnBehalfOf?.value
          });
        }
        handleChangeBatched!(initialStateChanges);
        setLoading(false);
        clearTimeout(timeout);
      }
    }
  }

  async function getActiveOrderNumbers() {
    if (!accountId) return;
    setLoading(true);
    const type = OrderNumberTypeSmartEnum.Recalls;
    const result = await httpGet(`api/accountOrderNumbers/listActiveByType?accountId=${accountId}&orderNumberType=${type.value}`);
    if (result.status === HttpStatusCodes.Ok) {
      const options = result.data.accountOrderNumbers.map((x: any) => ({
        value: x.id,
        text: x.number
      }));
      setActiveOrderNumbers(options);
      if (options.length > 0 && values?.enterOrderNumberManually === undefined) {
        handleChange!('enterOrderNumberManually')(false);
      }
      if (options.length === 0) {
        handleChange!('enterOrderNumberManually')(true);
      }
    }
    setLoading(false);
  }

  function loadComponent(component: ReactElement) {
    if (loading) {
      return (
        <div className="recall-delivery__field">
          <div className="loaders loaders__panel" />
        </div>
      )
    }
    return component;
  }

  function handleBack() {
    prevStep!();
  }

  function handleSubmit() {
    modalSignal.value = <ConfirmModal
      ContinueFunc={confirmSubmit}
      heading="Submit recall?"
      information={`You will not be able to adjust your recall once it has been submitted. You will shortly receive a confirmation email`} />
  }

  async function confirmSubmit() {
    const deliveryAddressData = deliveryAddresses.get(values?.deliveryAddress);
    const invoiceAddressData = invoiceAddresses.get(values?.invoiceAddress);
    let orderNumber = values?.orderNumber ?? '';
    if (activeOrderNumbers.length > 0 && !values?.enterOrderNumberManually) {
      const selectedOption = activeOrderNumbers.find(x => x.value === orderNumber);
      orderNumber = selectedOption?.text ?? '';
    }
    const payload = {
      recallId: values?.recallId,
      deliveryMethod: values?.deliveryMethod,
      deliveryAddress: deliveryAddressData,
      invoiceAddress: invoiceAddressData,
      onBehalfOfUser: values?.onBehalfOfUser,
      orderNumber: orderNumber,
      sendToEmail: values?.sendTo,
      isDigital: values?.isDigital
    }
    const result = await httpPost("api/recalls/submit", payload);
    if (result.status === HttpStatusCodes.Ok) {
      window.history.back();
    }
  }

  return (
    <div>
      <div className={`recall-delivery__wrapper ${loading ? 'recall-delivery__wrapper--loading' : ''}`}>
        {!values!.isDigital &&
          loadComponent(<FormAddressSelector
            label="Delivery Address"
            required={true}
            value={values?.deliveryAddress ?? (deliveryAddressOptions?.[0]?.value)}
            options={deliveryAddressOptions}
            addresses={deliveryAddresses}
            onChange={(id: string) => handleChange!("deliveryAddress")(id)} />)
        }
        {values!.isDigital && loadComponent(<AdobeTextField
          value={values?.sendTo ?? allDeliveryOptions?.sendTo}
          onChangeText={handleChange!("sendTo")}
          label="Send To Email"
          visibleLabel={true}
          required={true}
          disabled={false}
          validateRule={ValidateEmail}
          validationError='Must be a valid email'
          EnterKeyPress={() => { }} />)
        }
        {loadComponent(<FormAddressSelector
          label="Invoice Address"
          required={true}
          value={values?.invoiceAddress}
          options={invoiceAddressOptions}
          addresses={invoiceAddresses}
          onChange={(id: string) => handleChange!("invoiceAddress")(id)} />)
        }
        {loadComponent(
          <AdobeComboBox
            value={values?.deliveryMethod}
            label={'Delivery Method'}
            options={allDeliveryOptions?.deliveryMethods}
            required={true}
            onChangeSelect={handleChange!("deliveryMethod")}
            selectionMode="single"
            disabled={false}
            labelVisible={true} />)
        }
        {loadComponent(<AdobeComboBox
          value={values?.onBehalfOfUser ?? allDeliveryOptions?.defaultOnBehalfOf?.value}
          label={'On behalf of'}
          options={allDeliveryOptions?.onBehalfOf}
          required={true}
          onChangeSelect={handleChange!("onBehalfOfUser")}
          selectionMode="single"
          disabled={false}
          labelVisible={true} />)
        }
        {
          loadComponent(
            activeOrderNumbers.length > 0 ? (
              <div className="recall-delivery__field display--flex flex-direction--row gap--s align-items--flex-end" style={{ flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                  {values?.enterOrderNumberManually ? (
                    <AdobeTextField
                      value={values?.orderNumber ?? ''}
                      onChangeText={handleChange!("orderNumber")}
                      label="Reference"
                      visibleLabel={true}
                      placeholder="E.g. a PO # or someone's name for reference"
                      required={true}
                      disabled={false}
                      EnterKeyPress={() => { }}
                    />
                  ) : (
                    <AdobeComboBox
                      value={values?.orderNumber}
                      label="Reference"
                      options={activeOrderNumbers}
                      required={true}
                      onChangeSelect={handleChange!("orderNumber")}
                      selectionMode="single"
                      disabled={false}
                      labelVisible={true}
                    />
                  )}
                </div>
                <AdobeValueToggle
                  value={values?.enterOrderNumberManually ?? false}
                  onChange={() => {
                    const nextManual = !values?.enterOrderNumberManually;
                    handleChangeBatched!([
                      { elementName: 'enterOrderNumberManually', value: nextManual },
                      { elementName: 'orderNumber', value: '' }
                    ]);
                  }}
                  label="Enter reference manually?"
                  isDisabled={() => false}
                />
              </div>
            ) : (
              <AdobeTextField
                value={values?.orderNumber}
                onChangeText={handleChange!("orderNumber")}
                label="Reference"
                visibleLabel={true}
                placeholder="E.g. a PO # or someone's name for reference"
                required={true}
                disabled={false}
                EnterKeyPress={() => { }}
              />
            )
          )
        }
      </div>
      <div className="recall-wizard__footer">
        <Button className=" button button--large button--secondary button--with-icon" onPress={handleBack}>
          <p className='button-text'>Back</p>
        </Button>
        <Button className=" button button--large button--primary button--with-icon" isDisabled={errors !== undefined && Object.keys(errors).length > 0} onPress={handleSubmit}>
          <p className='button-text'>Submit recall</p>
        </Button>
      </div>
    </div>
  );
};

RecallStep3.validate = (values: Record<string, any>) => {
  const errors: Record<string, string> = {};

  if (values?.isDigital === false && !values.deliveryAddress) {
    errors.deliveryAddress = 'Must choose a delivery address';
  }
  if (values!.isDigital && !values.sendTo) {
    errors.deliveryAddress = 'Must choose who to send the recall to';
  }
  if (!values.invoiceAddress) {
    errors.deliveryAddress = 'Must choose an invoice address';
  }
  if (!values.deliveryMethod) {
    errors.deliveryAddress = 'Must choose a delivery method';
  }
  if (!values.onBehalfOfUser) {
    errors.deliveryAddress = 'Must choose on behalf of';
  }
  if (!values?.orderNumber?.toString().trim()) {
    errors.orderNumber = 'Must enter a client reference';
  }
  return errors;
};

export default RecallStep3;
